import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { HospitalReferralForm, type HospitalReferralPatient } from '@/components/patient/HospitalReferralForm';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { REFERRAL_OUTBOX_KINDS, useReferralOutbox } from '@/components/patient/referralOutboxData';
import { PatientHenvisningSentList } from '@/components/patient/PatientHenvisningSentList';
import { PatientOutboundReferralDetail } from '@/components/patient/PatientOutboundReferralDetail';
import { PatientReferralLetterTab } from '@/components/patient/PatientReferralLetterTab';
import { RadiologyReferralForm } from '@/components/patient/RadiologyReferralForm';
import { SpecialistReferralForm } from '@/components/patient/SpecialistReferralForm';
import type { ReferralCreatedInfo } from '@/components/patient/ReferralBuiltPreview';
import { usePatientReferralLetters } from '@/hooks/useReferrals';
import { supabase } from '@/lib/supabase';
import {
  DEFAULT_HENVISNING_BOX,
  patientHenvisningNewPath,
  patientHenvisningPath,
  type PatientRecordView,
} from '@/lib/patientRecordPaths';

export function PatientHenvisningWorkspace({
  clinicId,
  patient,
  view,
  revealedCpr,
  onCprRevealed,
  canRevealCpr,
  onCancel,
  onCreated,
}: {
  clinicId: string;
  patient: HospitalReferralPatient;
  view: PatientRecordView;
  revealedCpr: string | null;
  onCprRevealed: (cpr: string) => void;
  canRevealCpr: boolean;
  onCancel: () => void;
  onCreated: (created: ReferralCreatedInfo) => void;
}) {
  switch (view.kind) {
    case 'henvisning-new':
      return (
        <ComposeForm
          type={view.type}
          clinicId={clinicId}
          patient={patient}
          revealedCpr={revealedCpr}
          onCprRevealed={onCprRevealed}
          canRevealCpr={canRevealCpr}
          onCancel={onCancel}
          onCreated={onCreated}
        />
      );
    case 'henvisning-detail':
      return (
        <PatientOutboundReferralDetail
          clinicId={clinicId}
          patientId={patient.id}
          outboxId={view.outboxId}
        />
      );
    case 'henvisning-list':
      return (
        <HenvisningList
          clinicId={clinicId}
          patientId={patient.id}
          box={view.box}
          createdId={view.createdId}
        />
      );
    case 'tab':
    case 'journal-write':
    case 'correspondence-new':
    case 'ggop-new':
      return (
        <HenvisningList
          clinicId={clinicId}
          patientId={patient.id}
          box={DEFAULT_HENVISNING_BOX}
          createdId={null}
        />
      );
    default: {
      const unexpected: never = view;
      return unexpected;
    }
  }
}

function HenvisningList({
  clinicId,
  patientId,
  box,
  createdId,
}: {
  clinicId: string;
  patientId: string;
  box: 'sendte' | 'modtagne';
  createdId: string | null;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const received = usePatientReferralLetters(patientId, clinicId);
  const sent = useReferralOutbox(clinicId, patientId);
  const receivedLoading = received.isLoading && !received.data;
  const sentLoading = sent.isLoading && !sent.data;
  const receivedCount = received.data?.length ?? 0;
  const sentCount = (sent.data?.rows ?? []).filter((row) => REFERRAL_OUTBOX_KINDS.includes(row.kind)).length;
  const switcher = (
    <div className="hv-seg" role="tablist" aria-label={t('patients.hospital_referral.box_label', 'Visning')}>
      <button
        type="button"
        role="tab"
        aria-selected={box === 'sendte'}
        onClick={() => navigate(patientHenvisningPath(patientId, { vis: 'sendte' }))}
      >
        {t('patients.hospital_referral.box_sent', 'Sendte')}
        <span className="n num">
          {sentLoading ? <Skeleton className="h-3 w-3" /> : sentCount}
        </span>
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={box === 'modtagne'}
        onClick={() => navigate(patientHenvisningPath(patientId))}
      >
        {t('patients.hospital_referral.box_received', 'Modtagne')}
        <span className="n num">
          {receivedLoading ? <Skeleton className="h-3 w-3" /> : receivedCount}
        </span>
      </button>
    </div>
  );

  if (box === 'modtagne') {
    return <PatientReferralLetterTab patientId={patientId} action={switcher} />;
  }

  return (
    <PatientHenvisningSentList
      clinicId={clinicId}
      patientId={patientId}
      highlightId={createdId}
      switcher={switcher}
    />
  );
}

function ComposeForm({
  type,
  clinicId,
  patient,
  revealedCpr,
  onCprRevealed,
  canRevealCpr,
  onCancel,
  onCreated,
}: {
  type: 'sygehus' | 'billeddiagnostik' | 'speciallaege';
  clinicId: string;
  patient: HospitalReferralPatient;
  revealedCpr: string | null;
  onCprRevealed: (cpr: string) => void;
  canRevealCpr: boolean;
  onCancel: () => void;
  onCreated: (created: ReferralCreatedInfo) => void;
}) {
  const queryClient = useQueryClient();
  async function handleCreated(created: ReferralCreatedInfo) {
    await queryClient.invalidateQueries({
      queryKey: ['hospital-referral-outbox', clinicId, patient.id],
    });
    if (created.outboxId) {
      onCreated(created);
      return;
    }
    if (created.exchangeId) {
      const { data } = await supabase
        .from('clinic_communicate_outbound_referrals')
        .select('id')
        .eq('clinic_id', clinicId)
        .eq('patient_id', patient.id)
        .eq('exchange_id', created.exchangeId)
        .maybeSingle();
      if (data?.id) {
        onCreated({ ...created, outboxId: data.id });
        return;
      }
    }
    onCreated(created);
  }

  const shared = { patient, revealedCpr, onCprRevealed, canRevealCpr, onCancel, onCreated: handleCreated };
  const form = (() => {
    switch (type) {
      case 'sygehus':
        return <HospitalReferralForm {...shared} />;
      case 'billeddiagnostik':
        return <RadiologyReferralForm {...shared} />;
      case 'speciallaege':
        return <SpecialistReferralForm {...shared} />;
      default: {
        const unexpected: never = type;
        return unexpected;
      }
    }
  })();
  return <Card>{form}</Card>;
}

export function henvisningCreatedPath(patientId: string): string {
  return patientHenvisningPath(patientId, { vis: 'sendte', created: 'latest' });
}

export function henvisningComposeHref(patientId: string, type: 'sygehus' | 'billeddiagnostik' | 'speciallaege') {
  return patientHenvisningNewPath(patientId, type);
}
